import React, { useRef, useEffect, useContext, createContext, useState, useCallback } from 'react';
import { PAUL_DOMAIN, PROTOCOL, DEVELOPMENT, STAGING } from 'constants';
import { getAuthorizationToken } from 'services/authorization_token';
import { getCurrentSubDomain } from 'services/sub_domain';
import isNil from 'lodash/isNil';
import isEmpty from 'lodash/isEmpty';
import { useSession } from 'hooks/use_context';
import axios from 'axios';
import igLogger from 'services/logger';
import { useRequest } from 'components/wizard/context/request/hook';
import { useSocket } from 'components/socket/provider';
import { useRequestNavigation } from 'hooks/use_request_navigation';
import { useNavigate } from 'react-router-dom';
import { ErrorNotification } from 'services/notifications';
import InitializingRequest from 'components/request/initializing_request';
import { useShare } from 'contexts/share/use_share';
const PaulContext = createContext({});

const TIMING_LABEL_STYLE = 'color:#9ca3af;font-weight:500;';
const TIMING_MONO_STYLE = 'font-family:monospace;';
const TIMING_TYPE_BADGES = { CPT: '🟣 CPT', REF: '🟢 REF', WV: '🔵 WV' };

const timingColor = (ms, [warn, slow]) => (ms < warn ? '#22c55e' : ms < slow ? '#f59e0b' : '#ef4444');

const timingTableRow = (key, entry) => {
  const { seq, type, name, order, kind, active, wall, ...segments } = entry;
  return {
    rowKey: seq ?? key,
    row: {
      type: TIMING_TYPE_BADGES[type] || type || '',
      name: name || key,
      order,
      kind,
      active,
      wall,
      ...segments,
    },
  };
};

const renderTimingReport = (source, time, timing) => {
  const computers = timing.computers || {};
  const phases = timing.phases || {};

  console.groupCollapsed(
    `%c⏱ PAUL timing%c ${source} %c${Math.round(time.total)}ms total · ${Math.round(time.compute)}ms compute`,
    'background:#7c3aed;color:#fff;padding:2px 6px;border-radius:4px;font-weight:600;',
    'color:inherit;font-weight:600;',
    'color:#9ca3af;',
  );

  // Summary: wait_input + queue + compute + emit + network = total
  const summaryFormat = [];
  const summaryStyles = [];
  ['wait_input', 'queue', 'compute', 'emit', 'network', 'total'].forEach((key) => {
    summaryFormat.push(`%c${key} %c${Math.round(time[key] ?? 0)}ms`);
    summaryStyles.push(TIMING_LABEL_STYLE, `color:${timingColor(time[key] ?? 0, [100, 300])};font-weight:600;`);
  });
  console.log(summaryFormat.join('  '), ...summaryStyles);
  if (time.network_split) {
    const { upstream, downstream, front_processing } = time.network_split;
    console.log(
      `%cnetwork split → upstream ${upstream}ms · downstream ${downstream}ms · front processing ${front_processing ?? 'n/a'}ms %c(upstream/downstream assume synced clocks — reliable in local dev only)`,
      'color:#9ca3af;font-weight:500;',
      'color:#6b7280;font-style:italic;',
    );
  }
  console.log(
    "%cactive = time measured inside the computer's own work · wall = start→stop elapsed, includes waiting for parallel siblings on the event loop · untracked = wall − active (unmeasured work)",
    'color:#6b7280;font-style:italic;',
  );

  // Phases are sequential: their walls sum to ~compute. untracked = wall not covered
  // by any computer segment (merges, saves, event-loop, ...) — colored by that value.
  const phaseEntries = Object.entries(phases).filter(([, phase]) => phase && typeof phase === 'object');
  if (phaseEntries.length > 0) {
    const maxWall = Math.max(...phaseEntries.map(([, phase]) => phase.wall || 0), 1);
    console.log('%cPhases (sequential — sums to compute)', 'font-weight:700;');
    phaseEntries.forEach(([key, phase]) => {
      const bar = '█'.repeat(Math.max(1, Math.round(((phase.wall || 0) / maxWall) * 30)));
      console.log(
        `%c${key.padEnd(24, ' ')}%c${bar}%c ${phase.wall}ms %c(active ${phase.active}ms · untracked ${phase.untracked}ms)`,
        TIMING_LABEL_STYLE + TIMING_MONO_STYLE,
        `color:${timingColor(phase.untracked || 0, [10, 40])};` + TIMING_MONO_STYLE,
        'font-weight:600;',
        TIMING_LABEL_STYLE,
      );
    });
  }

  // updateData breakdown: where the emit-phase workflow.updateData spends its time
  const updateData = timing.update_data;
  if (updateData && typeof updateData === 'object') {
    console.log(`%cupdateData breakdown (emit phase) — ${updateData.wall}ms`, 'font-weight:700;');
    console.table(updateData.sections);
    if (updateData.slowestSteps && Object.keys(updateData.slowestSteps).length > 0) {
      console.groupCollapsed('slowest steps (own getMetaData time)');
      console.table(updateData.slowestSteps);
      console.groupEnd();
    }
  }

  // Computers: structured entries from paul (dev users) render as sortable tables
  const computerEntries = Object.entries(computers);
  const structured = computerEntries.length > 0 && computerEntries.every(([, entry]) => entry && typeof entry === 'object');
  if (structured) {
    const rows = {};
    computerEntries.forEach(([key, entry]) => {
      const { rowKey, row } = timingTableRow(key, entry);
      rows[rowKey] = row;
    });
    console.log('%cComputers', 'font-weight:700;');
    console.table(rows);
  } else if (computerEntries.length > 0) {
    console.log(computers);
  }

  console.groupCollapsed('raw timing');
  console.log(timing);
  console.groupEnd();

  console.groupEnd();
};

const PaulProvider = ({
  children,
  requestId,
  onComputed,
  onInitializedRequest,
  onRequestMetadata,
  onRequestError,
  onBusy,
  onNewNavigation,
  onRestrictedAccess,
  onTechnicalIssue,
  onWorkflowError,
  onRequestGenericData,
  onDocuments,
}) => {
  const navigate = useNavigate();
  const { socket } = useSocket();
  const [connecting, setConnecting] = useState(true);
  const socketRef = useRef(socket);
  const { currentUser } = useSession();
  const { inShare } = useShare();
  const { request, setCurrentEditor } = useRequest();
  const { forBackoffice } = useRequestNavigation();
  const [initProgress, setInitProgress] = useState();
  const [currentStepFromPaul, setCurrentStepFromPaul] = useState({});
  const onResolvedConflictsRef = useRef(null);
  const onBeforeApplyFieldsRef = useRef(null);
  const computedArrivedAtRef = useRef(null);

  const setOnResolvedConflicts = useCallback((fn) => {
    onResolvedConflictsRef.current = fn || null;
  }, []);

  const computeTime = (source, time, timing) => {
    if (DEVELOPMENT || STAGING) {
      renderTimingReport(source, time, timing);
    } else {
      igLogger.log('PAUL', 'Compute time', source, time);
    }
  };

  const updateNavigation = (newNavigation, from = '') => {
    if (newNavigation.currentStep) {
      igLogger.log('PAUL', 'updateCurrentStep', newNavigation.currentStep);
      setCurrentStepFromPaul(newNavigation.currentStep);
    }
    onNewNavigation(newNavigation);
  };

  const onFormValueChange = (newFormValues, stepId, mergeNewFormValues = false) => {
    igLogger.log('PAUL', '---> onFormValueChange', newFormValues);

    socketRef.current.emit('on-value-change', {
      newFormValues: {
        ...newFormValues,
        __metadata: {
          ...newFormValues.__metadata,
          sent_at: Date.now(),
        },
      },
      stepId,
      requestId: requestId,
      merge: mergeNewFormValues,
      forBackoffice,
    });
  };

  const computeNextSteps = (stepId, onComputed) => {
    igLogger.log('PAUL', '---> Compute next steps', stepId);
    socketRef.current.emit(
      'compute-next-steps',
      {
        stepId,
        forBackoffice,
      },
      () => {
        igLogger.log('PAUL', 'compute-next-steps finished');
        onComputed && onComputed();
      },
    );
  };

  const recomputePremiumsTableWithApi = (blockId, reset = false, callback) => {
    socketRef.current.emit(
      'compute-premiums-table-with-api',
      {
        blockId,
        reset,
        forBackoffice,
      },
      (response) => {
        const { request, navigation } = response;
        !isNil(request) && onInitializedRequest(request, '[RECOMPUTE WITH API RESPONSE]');
        !isNil(navigation) && updateNavigation(navigation, '[RECOMPUTE WITH API RESPONSE]');
        callback && callback(response);
      },
    );
  };

  const triggerEvent = (newFormValues, blockId, iteration) => {
    return new Promise((resolve, reject) => {
      socketRef.current.emit(
        'trigger-event',
        {
          newFormValues,
          blockId,
          iteration,
          forBackoffice,
        },
        (response) => {
          igLogger.log('PAUL', 'TRIGGER EVENT RESPONSE', response);
          if (!isNil(response)) {
            const { request, navigation, error, documents } = response;
            !isNil(request) && onInitializedRequest(request, '[TRIGGER EVENT]');
            !isNil(navigation) && updateNavigation(navigation, '[TRIGGER EVENT]');
            !isNil(documents) && onDocuments(documents);
            if (error && error === true) {
              reject();
            }
          }
          resolve(response);
        },
      );
    });
  };

  const reloadWorkflow = () => {
    return new Promise((resolve) => {
      igLogger.log('PAUL', '---> Reload workflow');
      socketRef.current.emit('reload-workflow', { forBackoffice }, ({ request_generic_data, navigation }) => {
        igLogger.log('PAUL', '<-- Reload workflow');
        onRequestGenericData(request_generic_data);
        !isNil(navigation) && updateNavigation(navigation, '[RELOAD WORKFLOW]');
        resolve(request);
      });
    });
  };

  const becomeEditor = () => {
    return new Promise((resolve) => {
      socketRef.current.emit(
        'become-editor',
        {
          forBackoffice,
        },
        ({ request, navigation }) => {
          igLogger.log('PAUL', '<-- Become editor', request, navigation);
          !isNil(request) && onInitializedRequest(request, '[BECOME EDITOR]');
          !isNil(navigation) && updateNavigation(navigation, '[BECOME EDITOR]');
          resolve(request);
        },
      );
    });
  };

  const prepareSign = (blockId, callback) => {
    socketRef.current.emit(
      'prepare-to-sign',
      {
        blockId,
        forBackoffice,
      },
      (response) => {
        igLogger.log('PAUL', 'PREPAPRE TO SIGN RESPONSE', response);
        callback && callback(response);
      },
    );
  };

  const sendToSign = (blockId, signerData, callback) => {
    socketRef.current.emit(
      'send-to-sign',
      {
        blockId,
        signerData,
        forBackoffice,
      },
      (response) => {
        igLogger.log('PAUL', 'SEND TO SIGN RESPONSE', response);
        callback && callback(response);
      },
    );
  };

  const cancelSign = (blockId, callback) => {
    socketRef.current.emit(
      'cancel-sign',
      {
        blockId,
        forBackoffice,
      },
      (response) => {
        igLogger.log('PAUL', 'CANCEL SIGN RESPONSE', response);
        callback && callback(response);
      },
    );
  };

  const checkSign = (blockId, callback) => {
    socketRef.current.emit(
      'check-sign',
      {
        blockId,
        forBackoffice,
      },
      (response) => {
        igLogger.log('PAUL', 'CHECK SIGN RESPONSE', response);
        callback && callback(response);
      },
    );
  };

  const sendManuallySignedDocuments = (blockId, signedDocuments) => {
    return new Promise((resolve) => {
      socketRef.current.emit(
        'send-manually-signed-document',
        {
          blockId,
          signedDocuments,
          forBackoffice,
        },
        (response) => {
          igLogger.log('PAUL', 'SEND MANUALLY SIGNED DOCUMENT RESPONSE', response);
          resolve(response);
        },
      );
    });
  };

  const recompute = () => {
    return new Promise((resolve) => {
      socketRef.current.emit('recompute', { forBackoffice }, ({ request, navigation }) => {
        igLogger.log('PAUL', '<-- Recompute');
        onInitializedRequest(request, '[RECOMPUTE]');
        updateNavigation(navigation, '[RECOMPUTE]');
        resolve(request);
      });
    });
  };

  const onRenderingError = (message) => {
    igLogger.log('PAUL', '--> Rendering error');
    socketRef.current.emit('rendering-error', { message });
  };

  const listAllStepFields = (stepId) => {
    socketRef.current.emit('list-all-step-fields', { stepId }, (response) => {
      igLogger.log('PAUL', '@allStepFields', response);
    });
  };

  const listAvailableData = async (workflowId) => {
    const url = `${PROTOCOL}://${PAUL_DOMAIN}/workflows/list_variables/${workflowId}`;
    const response = await axios.get(url, {
      headers: {
        Authorization: `Bearer ${getAuthorizationToken()}`,
      },
    });
    return response.data.variables;
  };

  const getNavigation = () => {
    socketRef.current.emit(
      'get-navigation',
      {
        forBackoffice,
      },
      (response) => {
        const { navigation } = response;
        igLogger.log('PAUL', '<-- Get navigation', navigation);
        !isNil(navigation) && updateNavigation(navigation, '[ACTIVE TAB CHANGE]');
      },
    );
  };

  const updateStatus = (statusId) => {
    socketRef.current.emit('update-status', { statusId });
  };

  const overwriteRequest = (newFormValues) => {
    socketRef.current.emit('overwrite-request', { newFormValues });
  };

  const initRequestCallback = () => {
    socketRef.current.emit(
      'init-request',
      {
        brandSlug: getCurrentSubDomain(),
        authorizationToken: getAuthorizationToken(),
        requestId,
        user: currentUser,
      },
      (response, error = null) => {
        igLogger.log('PAUL', 'initRequestCallback');
        if (error) {
          igLogger.log('PAUL', 'request initialisation error', error);
          if (error === 'restricted_access') {
            setConnecting(false);
            return onRestrictedAccess();
          } else if (error === 'technical_issue') {
            setConnecting(false);
            return onTechnicalIssue();
          }
          onRequestError(
            new ErrorNotification({
              message: `initialization error: ${error?.message || error}`,
              requestId,
              userId: currentUser.id,
              userEmail: currentUser.email,
            }),
          );
        } else {
          if ((isEmpty(response) || isEmpty(response.request) || isEmpty(response.navigation)) && response?.no_request_data !== true) {
            const description = isEmpty(response) ? 'No response' : isEmpty(response.request) ? 'No request received' : 'No navigation received';
            const message = `[Paul] request initialization error : ${description}`;
            onRequestError(
              new ErrorNotification({
                message: message,
                requestId,
                userId: currentUser.id,
                userEmail: currentUser.email,
              }),
            );
          } else {
            const { request, navigation, current_editor, request_generic_data, documents } = response;
            igLogger.log('PAUL', 'request initialized', request, navigation, documents);
            onRequestGenericData(request_generic_data);
            onDocuments(documents);
            igLogger.log('PAUL', 'request generic data', request_generic_data);
            onInitializedRequest(request, '[INIT]', request.workflow.lib_version === 2 ? getRequestData : null);
            updateNavigation(navigation, '[INIT]');
            setCurrentEditor(current_editor);
          }
        }
        setConnecting(false);
      },
    );
  };

  const onComputedCallback = ({ data, navigation, request_id }, source) => {
    if (request_id !== requestId) {
      return;
    }
    computedArrivedAtRef.current = Date.now();
    if (data && Object.keys(data).length === 1 && !isNil(data.__metadata)) {
      igLogger.log('PAUL', 'got only metadata');
      return;
    }
    igLogger.log('PAUL', '<--- onComputed', source, data, navigation);
    navigation && updateNavigation(navigation, '[COMPUTED]');
    data && onComputed(data);
  };

  const onMetadataCallback = ({ request_id, ...requestMetadata }) => {
    if (request_id !== requestId) {
      return;
    }
    igLogger.log('PAUL', 'Got new request metadata', requestMetadata);
    onRequestMetadata(requestMetadata);
  };

  const onBusyCallback = ({ request_id, busy }) => {
    if (request_id !== requestId) {
      return;
    }
    onBusy(busy);
  };
  const onWorkflowLanksyErrorCallback = ({ request_id, message }) => {
    igLogger.log('PAUL', 'Workflow lansky error', message);
    if (request_id !== requestId) {
      return;
    }
    onWorkflowError(message);
  };

  const onForceLeaveCallback = ({ request_id }) => {
    igLogger.log('PAUL', '<--- forceLeave', request_id);
    if (request_id !== requestId) {
      return;
    }
    navigate('/requests');
  };

  const onForceReloadCallback = () => {
    igLogger.log('PAUL', '<--- forceReload');
    window.location.reload();
  };

  const onInitProgressCallback = ({ request_id, progress, metadata }) => {
    if (request_id !== requestId) {
      return;
    }
    let duration = 0;
    if (metadata && metadata.duration) {
      duration = Math.round(metadata.duration / 1000);
    }
    igLogger.log('PAUL', '<--- onInitProgress', progress, duration > 0 ? `(took ${duration}s)` : '');
    setInitProgress(progress);
  };

  const onTimingCallback = ({ source, timing }) => {
    const receivedAt = Date.now();
    // Real debounce when sent_at was echoed back; fall back to the assumed 300ms otherwise
    const realWaitInput = timing.sent_at && timing.changed_at ? timing.sent_at - timing.changed_at : null;
    const timingDelta = timing.changed_at ? { wait_input: realWaitInput ?? 300 } : {};
    timingDelta.queue = timing.paul_compute - timing.paul_received;
    timingDelta.compute = timing.paul_emit - timing.paul_compute;
    timingDelta.emit = timing.paul_done - timing.paul_emit;
    const total = receivedAt - (timing.changed_at || timing.paul_received);
    timingDelta.network = total - timingDelta.queue - timingDelta.compute - timingDelta.emit - (timingDelta.wait_input || 0);
    // sometimes there's no changed_at (computeNextSteps or computeCR without changed_at)
    // fallback on paul_received and don't include the 300ms for the onValueChange debounce
    timingDelta.total = total;
    if (timing.sent_at) {
      const upstream = timing.paul_received - timing.sent_at;
      const frontProcessing = computedArrivedAtRef.current ? receivedAt - computedArrivedAtRef.current : null;
      timingDelta.network_split = {
        upstream,
        downstream: Math.round(timingDelta.network - upstream - (frontProcessing || 0)),
        front_processing: frontProcessing,
      };
    }
    computedArrivedAtRef.current = null;
    computeTime(source, timingDelta, timing);
  };

  const startGenie = (document, onGenieProgress) => {
    socketRef.current.emit('start-genie', { document });
    socketRef.current.on('genie-progress', onGenieProgress);
  };
  const stopGenie = (onGenieProgress) => {
    socketRef.current.off('genie-progress', onGenieProgress);
  };

  const touchStep = (parentStepId, stepId) => {
    socketRef.current.emit('touch-step', { parentStepId, stepId }, ({ navigation }) => {
      updateNavigation(navigation, '[TOUCH STEP]');
    });
  };

  const toggleWatchComputer = (computerId) => {
    igLogger.log('PAUL', '---> Watch computer', computerId);
    socketRef.current.emit('debug-computer', { computerId });
  };

  const onPaulDebuggerCallback = (data) => {
    const style = 'background: orange; color: #fff; padding: 2px 4px; border-radius: 2px; font-weight: bold;';
    igLogger.log('PAUL', `%c[Paul DEBUGGER] -------- start`, style);
    igLogger.dir('PAUL', data);
    igLogger.log('PAUL', `%c[Paul DEBUGGER] -------- end`, style);
  };

  const getWatchList = () => {
    return new Promise((resolve) => {
      socketRef.current.emit('get-watch-list', (response) => {
        resolve(response);
      });
    });
  };

  const onCheckChangesCallback = ({ kind, data }) => {
    //igLogger.log('PAUL', '<--- onCheckChanges', kind, data);
    const computer = data.computer;
    if (kind === 'no_change') {
      igLogger.log('COMPUTE_PREVENTED', `PREVENTING COMPUTE ${computer}`);
    } else {
      igLogger.log('WILL_COMPUTE', `WILL COMPUTE ${computer}`, data);
    }
  };

  const setCurrentStep = (stepId) => {
    igLogger.log('PAUL', '---> Set current step', stepId);
    if (isNil(stepId)) {
      setCurrentStepFromPaul({});
      return;
    }
    return new Promise((resolve) => {
      socketRef.current.emit('set-current-step', { stepId }, (response) => {
        igLogger.log('PAUL', '<-- Set current step', response);
        setCurrentStepFromPaul(response);
        resolve(response);
      });
    });
  };

  const getResume = () => {
    return new Promise((resolve) => {
      socketRef.current.emit('get-resume', (response) => {
        resolve(response);
      });
    });
  };

  const getRequestData = () => {
    new Promise((resolve) => {
      socketRef.current.emit('get-request-data', (response) => {
        console.log(response.request);
        resolve(response.request);
      });
    });
  };

  const onResolvedConflictsCallback = ({ fields, serverTs }) => {
    igLogger.log('PAUL', '<--- onResolvedConflicts', fields, serverTs);
    try {
      const mergedFields = onBeforeApplyFieldsRef.current ? onBeforeApplyFieldsRef.current({ fields, serverTs }) : fields;
      onResolvedConflictsRef.current?.(mergedFields);
    } catch (e) {
      igLogger.log('PAUL', '[onResolvedConflicts] handler error', e);
    }
  };

  const onUpdateNavigationCallback = (navigation) => {
    igLogger.log('PAUL', '<--- onNewNavigation', navigation);
    updateNavigation(navigation, '[NEW NAVIGATION]');
  };

  const setOnBeforeApplyFields = (fn) => {
    onBeforeApplyFieldsRef.current = fn;
  };

  const clearListeners = () => {
    socketRef.current.removeAllListeners('connect', initRequestCallback);
    socketRef.current.removeAllListeners('timing', onTimingCallback);
    socketRef.current.removeAllListeners('init-progress', onInitProgressCallback);
    socketRef.current.removeAllListeners('computed', onComputedCallback);
    socketRef.current.removeAllListeners('metadata', onMetadataCallback);
    socketRef.current.removeAllListeners('busy', onBusyCallback);
    socketRef.current.removeAllListeners('workflow_lansky_error', onWorkflowLanksyErrorCallback);
    socketRef.current.removeAllListeners('force-leave', onForceLeaveCallback);
    socketRef.current.removeAllListeners('force-reload', onForceReloadCallback);
    socketRef.current.removeAllListeners('ig-paul-debugger', onPaulDebuggerCallback);
    socketRef.current.removeAllListeners('check_changes', onCheckChangesCallback);
    socketRef.current.removeAllListeners('on-resolved-conflicts', onResolvedConflictsCallback);
    socketRef.current.removeAllListeners('update-navigation', onUpdateNavigationCallback);
  };

  useEffect(() => {
    socketRef.current.on('connect', initRequestCallback);
    socketRef.current.on('init-progress', onInitProgressCallback);
    socketRef.current.on('timing', onTimingCallback);
    socketRef.current.on('computed', onComputedCallback);
    socketRef.current.on('metadata', onMetadataCallback);
    socketRef.current.on('busy', onBusyCallback);
    socketRef.current.on('workflow_lansky_error', onWorkflowLanksyErrorCallback);
    socketRef.current.on('force-leave', onForceLeaveCallback);
    socketRef.current.on('force-reload', onForceReloadCallback);
    socketRef.current.on('ig-paul-debugger', onPaulDebuggerCallback);
    socketRef.current.on('check_changes', onCheckChangesCallback);
    socketRef.current.on('on-resolved-conflicts', onResolvedConflictsCallback);
    socketRef.current.on('update-navigation', onUpdateNavigationCallback);
    if (inShare) {
      initRequestCallback();
    }

    return () => {
      clearListeners();
      socketRef.current.emit('leave-request', { requestId });
    };
  }, []);

  window.addEventListener('beforeunload', (event) => {
    clearListeners();
    socketRef.current.emit('leave-request', { requestId });
    socketRef.current.disconnect();
  });

  return (
    <PaulContext.Provider
      value={{
        triggerEvent,
        recomputePremiumsTableWithApi,
        onFormValueChange,
        computeNextSteps,
        recompute,
        reloadWorkflow,
        becomeEditor,
        prepareSign,
        sendToSign,
        cancelSign,
        checkSign,
        sendManuallySignedDocuments,
        listAvailableData,
        onRenderingError,
        setOnResolvedConflicts,
        getNavigation,
        updateStatus,
        listAllStepFields,
        startGenie,
        stopGenie,
        touchStep,
        toggleWatchComputer,
        getWatchList,
        overwriteRequest,
        setCurrentStep,
        currentStep: currentStepFromPaul,
        setOnBeforeApplyFields,
        getResume,
      }}
    >
      {connecting ? <InitializingRequest progress={initProgress} isLoading={connecting} /> : children}
    </PaulContext.Provider>
  );
};

export default PaulProvider;
export const usePaul = () => useContext(PaulContext);
